/**
 * tdesign v1.0.3
 * (c) 2023 tdesign
 * @license MIT
 */

import { _ as _defineProperty } from '../_chunks/dep-46de7b2e.js';
import { _ as _slicedToArray } from '../_chunks/dep-e43cf21c.js';
import React, { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import useSwitch from '../_util/useSwitch.js';
import useAnimation from '../_util/useAnimation.js';
import Portal from '../common/Portal.js';
import useConfig from '../hooks/useConfig.js';
import getPosition from '../_common/js/utils/getPosition.js';
import { tooltipLiteDefaultProps } from './defaultProps.js';
import { getTransitionParams } from '../popup/utils/transition.js';
import '../_chunks/dep-2acfafca.js';
import '../_util/usePersistFn.js';
import '../config-provider/ConfigContext.js';
import '../_chunks/dep-d71ee5f3.js';
import '../_chunks/dep-c3f1141e.js';
import '../_chunks/dep-4b8f240e.js';
import '../_chunks/dep-9acbc163.js';
import '../_chunks/dep-7b52f080.js';
import '../_chunks/dep-f10515e8.js';
import '../_chunks/dep-4941ebe7.js';
import '../_chunks/dep-e9b62d1f.js';
import '../_chunks/dep-88cd50bf.js';
import '../_chunks/dep-dcb5aa2a.js';
import '../_chunks/dep-ee4bf8c2.js';
import '../_chunks/dep-934eda0c.js';
import '../_chunks/dep-7dce2c26.js';
import '../_chunks/dep-12510d64.js';
import '../_chunks/dep-6b568d83.js';
import '../_chunks/dep-304e657b.js';
import '../_chunks/dep-77e34309.js';
import '../_chunks/dep-6506fbc2.js';
import '../_chunks/dep-bb5d7b1e.js';
import '../_chunks/dep-e285f2f8.js';
import '../_chunks/dep-f4a320cd.js';
import '../_chunks/dep-ec70b95c.js';
import '../locale/zh_CN.js';
import '../_common/js/global-config/locale/zh_CN.js';
import 'dayjs';
import '../_chunks/dep-cbcc9f89.js';
import '../_common/js/global-config/default-config.js';
import 'react-dom';
import '../_util/dom.js';
import 'raf';
import '../_chunks/dep-6c5254c6.js';
import '../_util/easing.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var DEFAULT_TRANSITION_TIMEOUT = 180;
var TooltipLite = function TooltipLite(props) {
  var _classnames;
  var style = props.style,
    className = props.className,
    placement = props.placement,
    showArrow = props.showArrow,
    theme = props.theme,
    children = props.children,
    triggerElement = props.triggerElement,
    content = props.content,
    showShadow = props.showShadow;
  var triggerRef = useRef(null);
  var contentRef = useRef(null);
  var popupRef = useRef(null);
  var _useConfig = useConfig(),
    classPrefix = _useConfig.classPrefix;
  var _useSwitch = useSwitch(),
    _useSwitch2 = _slicedToArray(_useSwitch, 2),
    hover = _useSwitch2[0],
    hoverAction = _useSwitch2[1];
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    clientX = _useState2[0],
    setHoverClientX = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    position = _useState4[0],
    setPosition = _useState4[1];
  var _useAnimation = useAnimation(),
    keepFade = _useAnimation.keepFade;
  useEffect(function () {
    if (triggerRef.current && contentRef.current) {
      setPosition(getPosition(triggerRef.current, contentRef.current, placement, clientX));
    }
  }, [triggerRef.current, contentRef.current, placement, hover]);
  var onSwitchHover = function onSwitchHover(action, e) {
    setHoverClientX(e.clientX);
    hoverAction.set(action === "on");
  };
  var showTipArrow = showArrow && placement !== "mouse";
  var getTriggerChildren = function getTriggerChildren(children2) {
    var appendProps = {
      ref: triggerRef,
      onMouseEnter: function onMouseEnter(e) {
        return onSwitchHover("on", e);
      },
      onMouseLeave: function onMouseLeave(e) {
        return onSwitchHover("off", e);
      }
    };
    if (! /*#__PURE__*/React.isValidElement(children2)) {
      return /*#__PURE__*/React.cloneElement( /* @__PURE__ */React.createElement("div", null, children2), _objectSpread({}, appendProps));
    }
    return /*#__PURE__*/React.cloneElement(children2, _objectSpread({}, appendProps));
  };
  return /* @__PURE__ */React.createElement("div", null, getTriggerChildren(children || triggerElement), hover && /* @__PURE__ */React.createElement(Portal, null, /* @__PURE__ */React.createElement(CSSTransition, _objectSpread({
    appear: true,
    timeout: {
      appear: DEFAULT_TRANSITION_TIMEOUT
    },
    "in": hover,
    nodeRef: popupRef
  }, getTransitionParams({
    classPrefix: classPrefix,
    fadeAnimation: keepFade
  })), /* @__PURE__ */React.createElement("div", {
    className: classNames("".concat(classPrefix, "-popup"), "".concat(classPrefix, "-tooltip"), (_classnames = {}, _defineProperty(_classnames, "".concat(classPrefix, "-tooltip--").concat(theme), theme), _defineProperty(_classnames, "".concat(classPrefix, "-tooltip--noshadow"), !showShadow), _classnames), className),
    style: {
      position: "absolute",
      left: position === null || position === void 0 ? void 0 : position.left,
      top: position === null || position === void 0 ? void 0 : position.top
    },
    "data-popper-placement": placement,
    ref: popupRef
  }, /* @__PURE__ */React.createElement("div", {
    className: classNames("".concat(classPrefix, "-popup__content"), _defineProperty({}, "".concat(classPrefix, "-popup__content--arrow"), showTipArrow)),
    ref: contentRef,
    style: style
  }, content, showTipArrow && /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-popup__arrow")
  }))))));
};
TooltipLite.displayName = "Tooltiplite";
TooltipLite.defaultProps = tooltipLiteDefaultProps;
var _TooltipLite = /*#__PURE__*/React.memo(TooltipLite);

export { _TooltipLite as default };
//# sourceMappingURL=TooltipLite.js.map
